import { motion } from "framer-motion";
import { Rise } from "@/components/motion/Reveal";

// Four numbered chapters — an editorial manifesto on ElectroMax's standards.
const CHAPTERS = [
  {
    n: "01",
    kicker: "The Standard",
    h: "Every wire pulled like it's our own home.",
    p: "We don't leave splices in a wall we'd be embarrassed to open a year later. Every conductor lands where it belongs, every panel schedule is legible from arm's length, and every ground bond is torqued to spec — not by feel.",
  },
  {
    n: "02",
    kicker: "The Permit",
    h: "Permitted or we don't touch it.",
    p: "Panel upgrades, EV circuits, service masts — filed with Technical Safety BC before the truck rolls. If a contractor tells you the permit is optional, they're telling you the inspection is optional. It isn't.",
  },
  {
    n: "03",
    kicker: "The Number",
    h: "The quote is the invoice.",
    p: "Flat-rate, written, signed. No mysterious 'materials adjustment' on invoice day, no hourly meter ticking while we figure it out. If something changes mid-job, you approve the delta before we lift a screwdriver.",
  },
  {
    n: "04",
    kicker: "The Crew",
    h: "Journeymen only, on your job.",
    p: "The person diagnosing your panel at 11pm is Red Seal-ticketed, not an apprentice sent solo with a call-back number. That's not a marketing sentence — it's a scheduling rule we don't break.",
  },
];

export default function Manifesto() {
  return (
    <section
      data-testid="manifesto-section"
      className="relative py-24 lg:py-40 border-t border-[#262626] bg-[#0A0A0A] overflow-hidden"
    >
      {/* editorial spine */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-y-0 left-1/2 hidden lg:block w-px bg-[#262626]"
      />

      <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-16">
        <Rise>
          <div className="mb-16 lg:mb-24 max-w-3xl">
            <div className="text-xs tracking-[0.24em] uppercase text-[#E60000] font-bold mb-5 inline-flex items-center gap-3">
              <span className="inline-block h-px w-8 bg-[#E60000]" />
              A Short Manifesto
            </div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-[76px] tracking-tighter font-black leading-[0.95]">
              Four rules we <br />
              <span className="text-white/40">don&rsquo;t break.</span>
            </h2>
            <p className="mt-6 text-white/70 text-base md:text-lg leading-relaxed max-w-xl">
              After thirteen years of pulling wire across Metro Vancouver, we&rsquo;ve
              learned the difference between a good electrician and one you actually
              call back. It comes down to these four.
            </p>
          </div>
        </Rise>

        <ol className="space-y-16 lg:space-y-28">
          {CHAPTERS.map((c, i) => {
            const isEven = i % 2 === 0;
            return (
              <motion.li
                key={c.n}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.95, ease: [0.22, 1, 0.36, 1] }}
                className="relative grid lg:grid-cols-12 gap-8 lg:gap-16 items-start"
              >
                {/* Number column */}
                <div
                  className={`lg:col-span-5 ${
                    isEven ? "lg:order-1" : "lg:order-2 lg:text-right"
                  }`}
                >
                  <div className="relative inline-block">
                    <motion.span
                      initial={{ opacity: 0, scale: 0.85 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      viewport={{ once: true }}
                      transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
                      className="font-display block text-[110px] sm:text-[160px] lg:text-[220px] leading-[0.82] tracking-tighter font-black text-transparent"
                      style={{
                        WebkitTextStroke: "1px rgba(255,255,255,0.35)",
                      }}
                    >
                      {c.n}
                    </motion.span>
                    <motion.span
                      initial={{ scaleX: 0 }}
                      whileInView={{ scaleX: 1 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.3, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                      style={{ transformOrigin: isEven ? "left" : "right" }}
                      className="block h-[3px] bg-[#E60000] w-32 mt-2"
                    />
                    <div className="mt-4 text-[10px] tracking-[0.28em] uppercase text-[#E60000] font-bold">
                      {c.kicker}
                    </div>
                  </div>
                </div>

                {/* Copy column */}
                <div className={`lg:col-span-7 ${isEven ? "lg:order-2" : "lg:order-1"}`}>
                  <h3 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tighter font-bold leading-[1.02]">
                    {c.h}
                  </h3>
                  <p className="mt-6 text-white/70 leading-relaxed max-w-xl text-base md:text-lg">
                    {c.p}
                  </p>
                </div>
              </motion.li>
            );
          })}
        </ol>
      </div>
    </section>
  );
}
